// RIBBON overflow menu — the "…" anchor + popover (R0, 2026-07-20).
//
// One component serves both faces of the collapse system:
//   • a group's corner "…" (overflow-only controls + the drawer link), and
//   • a COLLAPSED group's labeled chip (all of its controls + the drawer link).
// Open/closed is pure UI state; outside-click closes (the Toolbar.jsx
// document-mousedown pattern). Position is absolute under the anchor inside
// .rbn-anchor — rows keep overflow visible so popovers never clip (the same
// lesson the live toolbar's CSS records).
import React, { useEffect, useRef, useState } from 'react';

export default function OverflowMenu({
  variant = 'corner',      // 'corner' (group "…") | 'chip' (collapsed group)
  label,                   // chip text / aria label
  title,                   // popover heading (group label)
  dot = false,             // chip carries the group's ACTIVE dot
  disabled = false,        // R1: disabled group — anchor inert, menu closed
  children,                // menu body (controls + optional footer)
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  // A group can disable while its menu is up (selection cleared) — close it.
  useEffect(() => {
    if (disabled) setOpen(false);
  }, [disabled]);

  useEffect(() => {
    if (!open) return undefined;
    const onDocDown = (ev) => {
      if (rootRef.current && !rootRef.current.contains(ev.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocDown);
    return () => document.removeEventListener('mousedown', onDocDown);
  }, [open]);

  const isChip = variant === 'chip';
  return (
    <span className="rbn-anchor" ref={rootRef}>
      <button
        type="button"
        className={isChip ? 'rbn-chip' : 'rbn-group-more'}
        aria-label={isChip ? `${label} (collapsed — open menu)` : `More in ${title}`}
        aria-expanded={open}
        title={isChip ? `${label} — collapsed into this menu` : `More ${title ? title.toLowerCase() : ''} options`}
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
      >
        {isChip && <span className="rbn-chip-label">{label}</span>}
        {isChip && dot ? <span className="rbn-dot" title="In use" aria-hidden="true" /> : null}
        <span className="rbn-ellipsis" aria-hidden="true">…</span>
      </button>
      {/* Navigation closes, adjustment stays: the drawer footer and menu
          actions dismiss on click; controls (sliders, chips…) keep the menu
          open for multi-adjust (house popover behavior). */}
      {open && (
        <div
          className="rbn-menu"
          role="menu"
          aria-label={title || label}
          data-menu-for={title || label}
          onClick={(ev) => {
            if (ev.target.closest('.rbn-menu-footer, .rbn-menu-action')) setOpen(false);
          }}
        >
          {title ? <div className="rbn-menu-title">{title}</div> : null}
          {children}
        </div>
      )}
    </span>
  );
}
